import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Gift, ArrowRight, Heart, Users, Sparkles, CreditCard, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GIFT_1 = 1800;
const GIFT_2 = 3420;

const GIFT_ADDONS = [
  { key: 'breakfast_basket', label: 'Surprise breakfast basket', desc: 'Fresh, colorful basket waiting in the BnB on arrival', price: 100, emoji: '🥐' },
  { key: 'handwritten_note', label: 'Handwritten note from Chanah', desc: 'A personal note welcoming her, written by Chanah herself', price: 200, emoji: '💌' },
  { key: 'signed_cookbook', label: 'Signed cookbook shipped early', desc: 'Chanah\'s cookbook signed and shipped to her before the retreat', price: 150, emoji: '📖' },
];

const GiftCareBnB = () => {
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    // Gifter
    gifterFirstName: '',
    gifterLastName: '',
    gifterEmail: '',
    gifterPhone: '',
    // Recipient
    recipientFirstName: '',
    recipientLastName: '',
    recipientEmail: '',
    recipientPhone: '',
    notifyRecipient: 'later',
    // Gift details
    numGuests: '1',
    personalMessage: '',
    giftAddons: [],
    // Consent
    consent: false,
  });

  const upd = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const toggleAddon = (key) => {
    setData((p) => ({
      ...p,
      giftAddons: p.giftAddons.includes(key)
        ? p.giftAddons.filter((k) => k !== key)
        : [...p.giftAddons, key],
    }));
  };

  const base = data.numGuests === '2' ? GIFT_2 : GIFT_1;
  const addonsTotal = useMemo(
    () => data.giftAddons.reduce((sum, k) => sum + (GIFT_ADDONS.find((a) => a.key === k)?.price || 0), 0),
    [data.giftAddons]
  );
  const amount = base + addonsTotal;
  const packageId = data.numGuests === '2' ? 'carebnb_gift_2guests' : 'carebnb_gift_1guest';

  const validate = () => {
    if (!data.gifterFirstName || !data.gifterLastName || !data.gifterEmail) {
      toast.error('Please share your name and email so I know who this beautiful gift is from');
      return false;
    }
    if (!data.recipientFirstName || !data.recipientLastName) {
      toast.error("Please share the recipient's name");
      return false;
    }
    if (!data.consent) {
      toast.error('Please confirm to submit');
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      const { data: gift } = await axios.post(`${API}/gift/carebnb`, data);
      toast.success('Redirecting to secure payment…');
      const { data: checkout } = await axios.post(`${API}/checkout/session`, {
        submission_id: gift.submission_id,
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="gift-page" data-testid="gift-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-content">
          <Link to="/">
            <img src="/brand/logo.png" alt="Momshiach" className="logo-img" />
          </Link>
          <nav className="desktop-nav">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/carebnb" className="nav-link">CareBnB</Link>
            <Link to="/resources" className="nav-link">Resources</Link>
          </nav>
          <div className="nav-actions">
            <a href="https://wa.me/972529326458" target="_blank" rel="noopener noreferrer" className="btn-secondary btn-contact">
              <Phone size={16} />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="gift-hero">
        <div className="container gift-hero-grid">
          <div>
            <div className="gift-eyebrow" data-testid="gift-eyebrow">
              <Gift size={14} />
              <span>Gift a Getaway · Give the gift of rest</span>
            </div>
            <h1 className="heading-hero" data-testid="gift-hero-title">
              Give her<br />what she can't give herself.
            </h1>
            <p className="body-large gift-hero-sub">
              For the mom drowning in Shabbos prep. For the friend who's lost in her own kitchen. For the daughter who needs a real reset — not another candle or bath salt.
            </p>
            <p className="body-medium">
              A CareBnB Shabbat Chavaya gift certificate: three days in Efrat, cooking with Chanah, learning what actually nourishes her, joining a warm community for Shabbos. She comes home restored.
            </p>
          </div>
          <div className="gift-hero-card">
            <div className="gift-icon-badge">
              <Heart size={28} />
            </div>
            <h3 className="heading-3">A gift that stays with her.</h3>
            <ul className="gift-includes">
              <li><Sparkles size={14} /> Private BnB, Thursday → Motzei Shabbos</li>
              <li><Sparkles size={14} /> Cooking lessons + published cookbook</li>
              <li><Sparkles size={14} /> Shabbos with Rabbi Shlomo Katz's community</li>
              <li><Sparkles size={14} /> Circle of Life coaching session</li>
              <li><Sparkles size={14} /> Beautiful printable gift certificate</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="gift-form-section">
        <div className="container container-narrow">
          <div className="section-header">
            <h2 className="heading-1">Send the gift</h2>
            <p className="body-large text-secondary">Full payment now — she pays nothing when she comes.</p>
          </div>

          <form onSubmit={submit} className="hc-form" data-testid="gift-form">
            {/* Gift package */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Gift Package</legend>
              <div className="guest-toggle">
                {['1', '2'].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => upd('numGuests', n)}
                    className={`guest-toggle-btn ${data.numGuests === n ? 'active' : ''}`}
                    data-testid={`gift-guests-${n}`}
                  >
                    <Users size={16} />
                    <span>{n === '1' ? '1 guest · Solo Reset' : '2 guests · Friends or Couples'}</span>
                    <strong>{(n === '1' ? GIFT_1 : GIFT_2).toLocaleString()} NIS</strong>
                  </button>
                ))}
              </div>
              <p className="caption" style={{ marginTop: 8 }}>Fully-paid gift. She uses the redemption code any time — no balance due on arrival.</p>
            </fieldset>

            {/* Gifter */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">From (you)</legend>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Your First Name *</label>
                  <input type="text" className="form-input" value={data.gifterFirstName} onChange={(e) => upd('gifterFirstName', e.target.value)} data-testid="gifter-first" />
                </div>
                <div className="form-group">
                  <label className="form-label">Your Last Name *</label>
                  <input type="text" className="form-input" value={data.gifterLastName} onChange={(e) => upd('gifterLastName', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Your Email *</label>
                  <input type="email" className="form-input" value={data.gifterEmail} onChange={(e) => upd('gifterEmail', e.target.value)} data-testid="gifter-email" />
                </div>
                <div className="form-group">
                  <label className="form-label">Your Phone / WhatsApp</label>
                  <input type="tel" className="form-input" value={data.gifterPhone} onChange={(e) => upd('gifterPhone', e.target.value)} />
                </div>
              </div>
            </fieldset>

            {/* Recipient */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">To (recipient)</legend>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Recipient First Name *</label>
                  <input type="text" className="form-input" value={data.recipientFirstName} onChange={(e) => upd('recipientFirstName', e.target.value)} data-testid="recipient-first" />
                </div>
                <div className="form-group">
                  <label className="form-label">Recipient Last Name *</label>
                  <input type="text" className="form-input" value={data.recipientLastName} onChange={(e) => upd('recipientLastName', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Recipient Email (optional)</label>
                  <input type="email" className="form-input" value={data.recipientEmail} onChange={(e) => upd('recipientEmail', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Recipient Phone (optional)</label>
                  <input type="tel" className="form-input" value={data.recipientPhone} onChange={(e) => upd('recipientPhone', e.target.value)} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">When should the gift be revealed?</label>
                <div className="checkbox-group">
                  {[
                    { v: 'now', label: 'Send her the certificate directly by email now' },
                    { v: 'later', label: "I'll print/share it myself when I'm ready" },
                  ].map((opt) => (
                    <label key={opt.v} className="checkbox-label">
                      <input type="radio" name="notify" checked={data.notifyRecipient === opt.v} onChange={() => upd('notifyRecipient', opt.v)} />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Personal message (goes on the certificate)</label>
                <textarea className="form-textarea" rows="3" maxLength="240" value={data.personalMessage} onChange={(e) => upd('personalMessage', e.target.value)} data-testid="gift-message" placeholder="e.g., You deserve to be poured back into. I love you." />
                <p className="caption" style={{ textAlign: 'right' }}>{data.personalMessage.length} / 240</p>
              </div>
            </fieldset>

            {/* Gift add-ons */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Make it extra special (optional)</legend>
              <p className="body-small text-muted" style={{ marginBottom: '1rem' }}>Tiny touches that turn a getaway into an unforgettable gift.</p>
              <div className="gift-addons">
                {GIFT_ADDONS.map((a) => {
                  const on = data.giftAddons.includes(a.key);
                  return (
                    <label key={a.key} className={`gift-addon ${on ? 'active' : ''}`} data-testid={`gift-addon-${a.key}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleAddon(a.key)} />
                      <div className="gift-addon-emoji">{a.emoji}</div>
                      <div className="gift-addon-body">
                        <strong>{a.label}</strong>
                        <p>{a.desc}</p>
                      </div>
                      <div className="gift-addon-price">
                        {on && <Check size={12} />}
                        <span>+{a.price} NIS</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Summary */}
            <div className="cb-cost-breakdown">
              <div className="cost-row">
                <span>Gift certificate ({data.numGuests === '2' ? '2 guests' : '1 guest'})</span>
                <strong>{base.toLocaleString()} NIS</strong>
              </div>
              {data.giftAddons.map((k) => {
                const a = GIFT_ADDONS.find((x) => x.key === k);
                return a ? (
                  <div className="cost-row" key={k}>
                    <span>{a.emoji} {a.label}</span>
                    <strong>+{a.price} NIS</strong>
                  </div>
                ) : null;
              })}
              <div className="cost-row total">
                <span>Total</span>
                <strong>{amount.toLocaleString()} NIS</strong>
              </div>
              <div className="cost-row" style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                <span>Balance on arrival</span><strong>0 NIS · Fully paid</strong>
              </div>
            </div>

            <label className="checkbox-label-block">
              <input type="checkbox" checked={data.consent} onChange={(e) => upd('consent', e.target.checked)} data-testid="gift-consent" />
              <span>I understand I'm paying {amount.toLocaleString()} NIS now to gift a CareBnB Shabbat Chavaya getaway. Chanah will follow up with the recipient to schedule dates.</span>
            </label>

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button type="submit" className="btn-primary btn-lg" disabled={submitting || !data.consent} data-testid="gift-submit">
                <CreditCard size={18} />
                <span>{submitting ? 'Processing…' : `Gift · Pay ${amount.toLocaleString()} NIS`}</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="caption">© 2025 Momshiach · Gift a CareBnB Getaway</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GiftCareBnB;
